import { useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Button, Skeleton, Stack, Typography } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { VolumeStatus } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { Link as RouterLink } from "react-router-dom";

import { FACILITY_ACCUMULATION_AREAS_TILE_QUERY } from "../../AccumulationAreas/api";
import { useWasteFacilityState } from "./WasteFacilityContext";

/**
 * Accumulation areas summary for the waste facility dashboard: the area
 * count, how many are over their volume threshold, and a link to the
 * facility-scoped areas page.
 */
export const WasteAccumulationAreasTile = () => {
  const { tenantId } = useTenant();
  const { effectiveWasteFacilityId } = useWasteFacilityState();

  const { data, loading } = useQuery(FACILITY_ACCUMULATION_AREAS_TILE_QUERY, {
    variables: { facilityId: effectiveWasteFacilityId },
    skip: !effectiveWasteFacilityId,
  });
  const items = data?.facility?.accumulationAreas?.items ?? [];
  const count = data?.facility?.accumulationAreas?.count;
  const overThresholdCount = items.filter(
    (area) => area.volume.status === VolumeStatus.Over
  ).length;

  return (
    <DashboardTile size={{ sm: 12, md: 6 }}>
      <DashboardTile.Header title="Accumulation areas" />
      <DashboardTile.ContentArea>
        <Stack spacing={2} alignItems="flex-start">
          {loading && count == null ? (
            <Skeleton variant="text" width={180} height={48} />
          ) : (
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography variant="h4">{count ?? 0}</Typography>
              <Typography variant="body1" color="text.secondary">
                {pluralize("area", count ?? 0)} · {overThresholdCount} over threshold
              </Typography>
            </Stack>
          )}
          <Button
            component={RouterLink}
            to={`/o/${tenantId}/waste/accumulation-areas?facilityId=${effectiveWasteFacilityId}`}
            variant="text"
            size="small"
            endIcon={<ArrowForward />}
          >
            View areas
          </Button>
        </Stack>
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
};
